import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Save, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { User } from "@supabase/supabase-js";
import { TIMEZONE_OPTIONS, getBrowserTimezone } from "@/lib/timezone";

interface ProfileData {
  first_name: string;
  last_name: string;
  phone: string;
  date_of_birth: string;
  sex: string;
  height: string;
  weight: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  zip_code: string;
  mailing_same_as_primary: boolean;
  mailing_address_line1: string;
  mailing_address_line2: string;
  mailing_city: string;
  mailing_state: string;
  mailing_zip_code: string;
  timezone: string;
}

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC"
];

const parseHeight = (h: string | null) => {
  if (!h) return { feet: "", inches: "" };
  const match = h.match(/(\d+)'(\d+)"/);
  if (match) return { feet: match[1], inches: match[2] };
  return { feet: "", inches: "" };
};

export default function ProfileSettings({ user }: { user: User }) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [heightFeet, setHeightFeet] = useState("");
  const [heightInches, setHeightInches] = useState("");

  const [profile, setProfile] = useState<ProfileData>({
    first_name: "",
    last_name: "",
    phone: "",
    date_of_birth: "",
    sex: "",
    height: "",
    weight: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zip_code: "",
    mailing_same_as_primary: true,
    mailing_address_line1: "",
    mailing_address_line2: "",
    mailing_city: "",
    mailing_state: "",
    mailing_zip_code: "",
    timezone: getBrowserTimezone(),
  });

  const [providerName, setProviderName] = useState<string | null>(null);
  const [lastCheckup, setLastCheckup] = useState<string | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      const { data } = await supabase
        .from("profiles")
        .select("first_name, last_name, phone, date_of_birth, sex, height, weight, address_line1, address_line2, city, state, zip_code, mailing_same_as_primary, mailing_address_line1, mailing_address_line2, mailing_city, mailing_state, mailing_zip_code, assigned_provider_id, last_checkup_date, timezone")
        .eq("user_id", user.id)
        .single();

      if (data) {
        const parsed = parseHeight(data.height);
        setHeightFeet(parsed.feet);
        setHeightInches(parsed.inches);
        setProfile({
          first_name: data.first_name || "",
          last_name: data.last_name || "",
          phone: data.phone || "",
          date_of_birth: data.date_of_birth || "",
          sex: data.sex || "",
          height: data.height || "",
          weight: data.weight ? String(data.weight) : "",
          address_line1: data.address_line1 || "",
          address_line2: data.address_line2 || "",
          city: data.city || "",
          state: data.state || "",
          zip_code: data.zip_code || "",
          mailing_same_as_primary: data.mailing_same_as_primary ?? true,
          mailing_address_line1: data.mailing_address_line1 || "",
          mailing_address_line2: data.mailing_address_line2 || "",
          mailing_city: data.mailing_city || "",
          mailing_state: data.mailing_state || "",
          mailing_zip_code: data.mailing_zip_code || "",
          timezone: (data as any).timezone || getBrowserTimezone(),
        });

        setLastCheckup(data.last_checkup_date || null);

        // Fetch provider name if assigned
        if (data.assigned_provider_id) {
          const { data: provData } = await supabase
            .from("profiles")
            .select("first_name, last_name")
            .eq("user_id", data.assigned_provider_id)
            .single();
          if (provData) {
            setProviderName(`${provData.first_name || ""} ${provData.last_name || ""}`.trim() || "Assigned Provider");
          }
        }
      }
      setLoading(false);
    };
    fetchProfile();
  }, [user.id]);

  const updateField = (field: keyof ProfileData, value: string | boolean) => {
    setProfile((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    const heightStr = heightFeet && heightInches ? `${heightFeet}'${heightInches}"` : null;

    const { error } = await supabase
      .from("profiles")
      .update({
        first_name: profile.first_name || null,
        last_name: profile.last_name || null,
        phone: profile.phone || null,
        date_of_birth: profile.date_of_birth || null,
        sex: profile.sex || null,
        height: heightStr,
        weight: profile.weight ? Number(profile.weight) : null,
        address_line1: profile.address_line1 || null,
        address_line2: profile.address_line2 || null,
        city: profile.city || null,
        state: profile.state || null,
        zip_code: profile.zip_code || null,
        mailing_same_as_primary: profile.mailing_same_as_primary,
        mailing_address_line1: profile.mailing_same_as_primary ? null : profile.mailing_address_line1 || null,
        mailing_address_line2: profile.mailing_same_as_primary ? null : profile.mailing_address_line2 || null,
        mailing_city: profile.mailing_same_as_primary ? null : profile.mailing_city || null,
        mailing_state: profile.mailing_same_as_primary ? null : profile.mailing_state || null,
        mailing_zip_code: profile.mailing_same_as_primary ? null : profile.mailing_zip_code || null,
        timezone: profile.timezone,
      } as any)
      .eq("user_id", user.id);

    setSaving(false);
    if (error) {
      toast({ title: "Error", description: "Failed to update profile.", variant: "destructive" });
    } else {
      toast({ title: "Profile updated", description: "Your changes have been saved." });
    }
  };

  if (loading) {
    return (
      <Card className="border-border bg-card">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Provider & Checkup Info (read-only) */}
      {(providerName || lastCheckup) && (
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-lg">Care Team</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {providerName && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div>
                  <p className="text-sm font-medium text-foreground">Assigned Provider</p>
                  <p className="text-sm text-muted-foreground">{providerName}</p>
                </div>
              </div>
            )}
            {lastCheckup && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div>
                  <p className="text-sm font-medium text-foreground">Last Checkup</p>
                  <p className="text-sm text-muted-foreground">
                    {new Date(lastCheckup).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Personal Info */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="text-lg">Personal Information</CardTitle>
          <CardDescription>Update your name, contact info, and health profile.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First name</Label>
              <Input id="firstName" value={profile.first_name} onChange={(e) => updateField("first_name", e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last name</Label>
              <Input id="lastName" value={profile.last_name} onChange={(e) => updateField("last_name", e.target.value)} />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Email</Label>
            <Input value={user.email || ""} disabled className="bg-muted" />
            <p className="text-xs text-muted-foreground">Contact support to change your email address.</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" type="tel" value={profile.phone} onChange={(e) => updateField("phone", e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Timezone</Label>
              <Select value={profile.timezone} onValueChange={(v) => updateField("timezone", v)}>
                <SelectTrigger><SelectValue placeholder="Select timezone" /></SelectTrigger>
                <SelectContent>
                  {TIMEZONE_OPTIONS.map((tz) => (
                    <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="dob">Date of birth</Label>
              <Input id="dob" type="date" value={profile.date_of_birth} onChange={(e) => updateField("date_of_birth", e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Biological sex</Label>
              <Select value={profile.sex} onValueChange={(v) => updateField("sex", v)}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Height</Label>
              <div className="flex gap-2">
                <Select value={heightFeet} onValueChange={setHeightFeet}>
                  <SelectTrigger className="flex-1"><SelectValue placeholder="Feet" /></SelectTrigger>
                  <SelectContent>
                    {[3, 4, 5, 6, 7].map((ft) => (
                      <SelectItem key={ft} value={String(ft)}>{ft} ft</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={heightInches} onValueChange={setHeightInches}>
                  <SelectTrigger className="flex-1"><SelectValue placeholder="In" /></SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 12 }, (_, i) => (
                      <SelectItem key={i} value={String(i)}>{i} in</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="weight">Weight (lbs)</Label>
              <Input id="weight" type="number" value={profile.weight} onChange={(e) => updateField("weight", e.target.value)} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Address */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="text-lg">Primary Address</CardTitle>
          <CardDescription>Your home or residential address.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="address1">Address line 1</Label>
            <Input id="address1" value={profile.address_line1} onChange={(e) => updateField("address_line1", e.target.value)} placeholder="Street address" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="address2">Address line 2</Label>
            <Input id="address2" value={profile.address_line2} onChange={(e) => updateField("address_line2", e.target.value)} placeholder="Apt, suite, unit (optional)" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <Input id="city" value={profile.city} onChange={(e) => updateField("city", e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>State</Label>
              <Select value={profile.state} onValueChange={(v) => updateField("state", v)}>
                <SelectTrigger><SelectValue placeholder="State" /></SelectTrigger>
                <SelectContent>
                  {US_STATES.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="zip">ZIP code</Label>
              <Input id="zip" value={profile.zip_code} onChange={(e) => updateField("zip_code", e.target.value)} maxLength={10} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Mailing Address */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="text-lg">Mailing Address</CardTitle>
          <CardDescription>Where prescriptions and correspondence are sent.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Switch
              checked={profile.mailing_same_as_primary}
              onCheckedChange={(v) => updateField("mailing_same_as_primary", v)}
            />
            <Label className="text-sm">Same as primary address</Label>
          </div>

          {!profile.mailing_same_as_primary && (
            <>
              <div className="space-y-2">
                <Label htmlFor="mAddress1">Address line 1</Label>
                <Input id="mAddress1" value={profile.mailing_address_line1} onChange={(e) => updateField("mailing_address_line1", e.target.value)} placeholder="Street address" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mAddress2">Address line 2</Label>
                <Input id="mAddress2" value={profile.mailing_address_line2} onChange={(e) => updateField("mailing_address_line2", e.target.value)} placeholder="Apt, suite, unit (optional)" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="mCity">City</Label>
                  <Input id="mCity" value={profile.mailing_city} onChange={(e) => updateField("mailing_city", e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>State</Label>
                  <Select value={profile.mailing_state} onValueChange={(v) => updateField("mailing_state", v)}>
                    <SelectTrigger><SelectValue placeholder="State" /></SelectTrigger>
                    <SelectContent>
                      {US_STATES.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mZip">ZIP code</Label>
                  <Input id="mZip" value={profile.mailing_zip_code} onChange={(e) => updateField("mailing_zip_code", e.target.value)} maxLength={10} />
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="gap-2">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save Changes
        </Button>
      </div>
    </div>
  );
}
